import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import Search from '@mui/icons-material/Search';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, styled, Menu, Typography, Collapse } from '@mui/material';

import Checkbox from '../Checkbox';
import SearchBar from '../SearchBar';
import NonIdealState from '../NonIdealState';
import BaseFilterButton from '../BaseFilterButton';
import { MultiSelectFilterProps } from './types';
import { useTheme } from '@mui/material/styles';
import {
  MULTI_SELECT_ITEM_HEIGHT,
  flattenOptions,
  getDisplayableInputValue,
  isValueEqualToDefaultValue,
  labelHasSearchTerm,
} from './helpers';
import { Option } from './types';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  icon,
  placeholder,
  options,
  menuTitle,
  onChange,
  value,
  variant = 'primary',
  onClose,
  defaultValue,
  onClear,
  menuWidth = 300,
  xAlign = 'left',
  isMenuSearchable = true,
  simplifyLabel = false,
  maxHeight = 480,
  fullWidth = false,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedValues, setExpandedValues] = useState<string[]>([]);
  const [isCheckboxClicked, setIsCheckboxClicked] = useState(false);

  const filteredOptions = useMemo(() => {
    const checkOptionForTerm = (option: Option): boolean => {
      const childHasSearchTerm =
        option.options && option.options.some(checkOptionForTerm);
      return childHasSearchTerm || labelHasSearchTerm(option, search);
    };
    return options.filter(checkOptionForTerm).map(option => {
      const optionChildren = labelHasSearchTerm(option, search)
        ? option.options
        : option.options?.filter(checkOptionForTerm);
      return {
        ...option,
        options: optionChildren,
      };
    });
  }, [search, options]);

  const selectedLabels = useMemo(() => {
    const renderTitle = (item: Option): string => {
      const itemOptionValues = flattenOptions(item.options ?? [])
        .filter(item => !item.options?.length)
        .map(item => item.value);

      const selectedLabel = value.includes(item.value) ? item.label : '';
      const hasSelectedChild = itemOptionValues.some(option =>
        value.includes(option),
      );

      if (!selectedLabel && !hasSelectedChild) {
        return '';
      }

      if (hasSelectedChild) {
        const childrenLabels =
          item?.options?.map(renderTitle).filter(Boolean).join(', ') ?? '';
        if (simplifyLabel) {
          return childrenLabels;
        }
        return `${item.label} (${childrenLabels})`;
      }

      return selectedLabel;
    };

    return options.map(renderTitle).filter(Boolean);
  }, [value, options, simplifyLabel]);

  const isOpen = Boolean(anchorEl);

  const handleOnClear = useCallback(() => {
    onClear && onClear();
    onChange(defaultValue ?? []);
  }, [defaultValue, onClear, onChange]);

  const handleClose = useCallback(() => {
    onClose && onClose();
    setAnchorEl(null);
    setExpandedValues([]);
    setSearch('');
  }, [onClose]);

  const handleCheckboxClick = (
    item: Option,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    e.stopPropagation();
    setIsCheckboxClicked(true);

    const hasOptions = Boolean(item.options);
    const itemOptionValues = flattenOptions(item.options ?? [])
      .filter(child => !child.options?.length)
      .map(child => child.value);
    const isChecked = hasOptions
      ? itemOptionValues.every(option => value.includes(option))
      : value.includes(item.value);

    // Condition 1: If unchecked and not expanded, expand
    if (!isChecked && hasOptions && !expandedValues.includes(item.value)) {
      setExpandedValues([...expandedValues, item.value]);
    }

    if (hasOptions) {
      if (isChecked) {
        onChange(value.filter(v => !itemOptionValues.includes(v)));
      } else {
        onChange([
          ...value,
          ...itemOptionValues.filter(v => !value.includes(v)),
        ]);
      }
    } else {
      if (isChecked) {
        onChange(value.filter(v => v !== item.value));
      } else {
        onChange([...value, item.value]);
      }
    }
  };

  const handleMenuItemClick = (item: Option) => {
    if (!isCheckboxClicked) {
      if (item.options?.length) {
        // Expand/collapse logic for items with children
        if (expandedValues.includes(item.value)) {
          setExpandedValues(expandedValues.filter(v => v !== item.value));
        } else {
          setExpandedValues([...expandedValues, item.value]);
        }
      } else {
        // Toggle select/deselect for item with no children
        if (value.includes(item.value)) {
          onChange(value.filter(v => v !== item.value));
        } else {
          onChange([...value, item.value]);
        }
      }
    } else {
      // If checkbox is clicked and item has no children, deselect it
      if (!item.options?.length) {
        onChange(value.filter(v => v !== item.value));
      }
    }

    setIsCheckboxClicked(false);
  };

  const noSearchResults = filteredOptions.length === 0;

  const renderListOption = (item: Option) => {
    const hasOptions = Boolean(item.options);
    const itemOptionValues = flattenOptions(item.options ?? [])
      .filter(child => !child.options?.length)
      .map(child => child.value);
    const isChecked = hasOptions
      ? itemOptionValues.every(option => value.includes(option))
      : value.includes(item.value);
    const isIndeterminate =
      !isChecked && itemOptionValues.some(option => value.includes(option));
    const isExpanded = expandedValues.includes(item.value);

    return (
      <>
        <MenuItem key={item.label} onClick={() => handleMenuItemClick(item)}>
          <Checkbox
            indeterminate={isIndeterminate}
            padding='4px 12px'
            aria-label={item.label}
            checked={isChecked}
            onChange={e => handleCheckboxClick(item, e)}
          />
          <ListItemText
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            primary={
              <Typography variant='font14' noWrap>
                {item.optionStartAdornment ? (
                  <Box display='flex' alignItems='center'>
                    <Box
                      mr='5px'
                      display='flex'
                      alignItems='center'
                      color={theme.palette.colors.grey[800]}
                    >
                      {item.optionStartAdornment}
                    </Box>
                    {item.label}
                  </Box>
                ) : (
                  item.label
                )}
              </Typography>
            }
          />
          {hasOptions ? (
            <IconButton
              sx={{
                transform: `rotate(${isExpanded ? '180' : '0'}deg)`,
              }}
              onClick={e => {
                e.stopPropagation();
                handleMenuItemClick(item);
              }}
            >
              <KeyboardArrowDown />
            </IconButton>
          ) : (
            <OnlyButton
              onClick={e => {
                e.stopPropagation();
                onChange([item.value]);
              }}
            >
              <Typography
                color={theme.palette.colors.purple[500]}
                variant='font12'
              >
                Only
              </Typography>
            </OnlyButton>
          )}
        </MenuItem>
        {hasOptions && (
          <Collapse in={isExpanded}>
            <Box marginLeft={2}>
              {item.options?.map(option => renderListOption(option))}
            </Box>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <>
      <BaseFilterButton
        value={getDisplayableInputValue({
          variant,
          value,
          placeholder,
          selectedLabels,
        })}
        isOpen={isOpen}
        showCaret={
          isValueEqualToDefaultValue(value, defaultValue) || !value.length
        }
        onClick={e => setAnchorEl(e.currentTarget)}
        onClear={handleOnClear}
        startAdornment={icon}
        aria-label='multi select filter button'
        size={fullWidth ? 'medium' : 'small'}
        overflowTooltipContent={selectedLabels.join(', ')}
      />
      <Menu
        slotProps={{
          paper: {
            elevation: 2,
            sx: {
              maxHeight: maxHeight,
              border: `1px solid ${theme.palette.colors.grey[500]}`,
              width: menuWidth,
            },
          },
        }}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        open={isOpen}
        MenuListProps={{
          variant: menuTitle ? 'menu' : 'selectedMenu',
          'aria-labelledby': 'basic-button',
          sx: {
            paddingTop: menuTitle ? 0 : 1,
            paddingBottom: 0,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: xAlign,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: xAlign,
        }}
        sx={{
          marginTop: '8px',
        }}
      >
        {menuTitle && (
          <Box padding='12px 16px' component='div' tabIndex={undefined}>
            <Typography variant='font16Bold' color={theme.palette.colors.black}>
              {menuTitle}
            </Typography>
          </Box>
        )}
        {isMenuSearchable && (
          <SearchBarContainer>
            <SearchBar
              size='small'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchBarContainer>
        )}
        <ItemContainer>
          {noSearchResults ? (
            <NoResultsContainer>
              <NonIdealState
                icon={<Search />}
                title='No results found'
              ></NonIdealState>
            </NoResultsContainer>
          ) : (
            filteredOptions.map(renderListOption)
          )}
        </ItemContainer>
      </Menu>
    </>
  );
};

export default MultiSelectFilter;

const SearchBarContainer = styled('div')`
  padding: 0 12px 12px 12px;
`;

const ItemContainer = styled('div')`
  width: 100%;
  flex: 1;
  margin-top: 5px;
`;

const OnlyButton = styled('button')`
  display: none;
  background: transparent;
  outline: none;
  border: none;
  border-radius: 4px;
  padding: 2px 4px 2px 4px;
  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.palette.colors.purple[200]};
  }
`;

const MenuItem = styled('div')`
  padding: 2px 16px;
  display: flex;
  height: ${MULTI_SELECT_ITEM_HEIGHT}px;
  margin-bottom: 4px;
  justify-content: space-between;
  align-items: center;
  &:hover {
    background-color: ${props => props.theme.palette.colors.purple[100]};
    cursor: pointer;
    button {
      display: flex;
    }
  }
`;

const NoResultsContainer = styled('div')`
  height: 174px;
`;
